import {
  type CSSProperties,
  type PropsWithChildren,
  type ReactNode,
  type RefObject,
  useEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { type ClassName, twx } from '@/utils/tailwind';

const SMALL_SCREEN_MEDIA = '(max-width: 479.98px)';

/** Hook that returns true when the viewport matches the small-screen
 *  breakpoint (< 480px). Re-runs on resize. SSR-safe: returns false on
 *  the server. */
const useIsSmallScreen = (): boolean => {
  const [isSmall, setIsSmall] = useState(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
    return window.matchMedia(SMALL_SCREEN_MEDIA).matches;
  });
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mq = window.matchMedia(SMALL_SCREEN_MEDIA);
    const onChange = (e: MediaQueryListEvent) => setIsSmall(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return isSmall;
};

export const menuPopoverAlign = ['left', 'right'] as const;
export type MenuPopoverAlign = (typeof menuPopoverAlign)[number];

const widthClasses = {
  xs: 'w-52 max-w-[calc(100vw-2rem)]',
  sm: 'w-72 max-w-[calc(100vw-2rem)]',
  base: 'w-80 max-w-[calc(100vw-2rem)]',
  lg: 'w-96 max-w-[calc(100vw-2rem)]',
  xl: 'w-[min(36rem,calc(100vw-2rem))]',
} as const;
export type MenuPopoverWidth = keyof typeof widthClasses;

export interface MenuPopoverProps extends PropsWithChildren {
  /** Render-prop for the trigger. Receives a ref + props to spread on the
   * trigger element so outside-click detection ignores it. */
  renderTrigger: (props: {
    ref: RefObject<HTMLButtonElement | null>;
    onClick: () => void;
    isOpen: boolean;
  }) => ReactNode;
  /** Anchor edge: `right` is the default for header chips so the panel
   * grows leftward instead of off-viewport. */
  align?: MenuPopoverAlign;
  width?: MenuPopoverWidth;
  /** Closes the popover when fired — call sites pass this through their
   * menu actions so the popover dismisses on selection. */
  onCloseAction?: () => void;
  className?: ClassName;
}

/**
 * Controlled-internally popover with anchored positioning + outside-click
 * dismiss. Consolidates the trigger+panel+useEffect plumbing repeated in
 * TripModeMenu and FiltersPopover.
 *
 * The popover is rendered in-place beneath the trigger using `absolute`
 * positioning — no portal — since all current usages live inside the
 * header chip context.
 */
export const MenuPopover = ({
  renderTrigger,
  align = 'right',
  width = 'base',
  onCloseAction: _,
  className,
  children,
}: MenuPopoverProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const isSmall = useIsSmallScreen();
  // Position the popover relative to the viewport on small screens —
  // anchored-to-trigger overflows in either direction depending on
  // where the trigger sits horizontally. Measured from the trigger's
  // bounding rect so the popover hangs immediately below the trigger.
  // Computed synchronously when the popover opens so the very first
  // render already has the correct inline style — otherwise the popover
  // briefly renders at its flow position before the effect catches up.
  const [smallScreenPos, setSmallScreenPos] = useState<{ top: number } | null>(null);

  const measureSmallScreen = () => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (!rect) return null;
    // Drop the popover ~6px below the trigger to mirror the absolute
    // `mt-1.5` (6px) gap we use on desktop.
    return { top: rect.bottom + 6 };
  };

  const handleToggle = () => {
    setIsOpen((wasOpen) => {
      const next = !wasOpen;
      // Opening AND on a small screen — capture the trigger position
      // immediately so the popover renders correctly on its first
      // paint, with no positional flash.
      if (next && isSmall) setSmallScreenPos(measureSmallScreen());
      else setSmallScreenPos(null);
      return next;
    });
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: measureSmallScreen is stable per-render and only reads ref
  useEffect(() => {
    if (!(isOpen && isSmall)) return;
    // Keep the popover position in sync with viewport resizes while it's
    // open. Throttled via requestAnimationFrame so a continuous resize
    // drag doesn't fire a measure-then-setState on every pixel — that
    // combination caused the dev server to freeze for several seconds
    // on each resize. Capture-phase scroll listeners are intentionally
    // omitted: the trigger sits inside the fixed header, so scroll
    // doesn't move it.
    let pending = false;
    const onResize = () => {
      if (pending) return;
      pending = true;
      window.requestAnimationFrame(() => {
        pending = false;
        setSmallScreenPos(measureSmallScreen());
      });
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [isOpen, isSmall]);

  useEffect(() => {
    if (!isOpen) return;
    const handleOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (triggerRef.current?.contains(target) || popoverRef.current?.contains(target)) return;
      setIsOpen(false);
    };
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, [isOpen]);

  const smallScreenStyle: CSSProperties | undefined = smallScreenPos
    ? { top: smallScreenPos.top, left: 8, right: 8 }
    : undefined;

  const smallScreenPopover =
    isOpen && isSmall
      ? // Portaled to document.body so ancestor stacking-context / containing-
        // block weirdness (backdrop-blur in the header, transforms anywhere
        // in the tree) doesn't trap the `position: fixed` element. The
        // measured `top` + `left:8/right:8` keeps it anchored to the
        // viewport just under the trigger.
        createPortal(
          <div
            ref={popoverRef}
            style={smallScreenStyle}
            className="fixed bg-white border border-content-border rounded-2xl shadow-xl z-prompt overflow-hidden"
          >
            <MenuPopoverContext.Provider value={() => setIsOpen(false)}>
              {children}
            </MenuPopoverContext.Provider>
          </div>,
          document.body,
        )
      : null;

  return (
    <div className={twx('relative', className)}>
      {renderTrigger({
        ref: triggerRef,
        onClick: handleToggle,
        isOpen,
      })}
      {smallScreenPopover}
      {isOpen && !isSmall && (
        <div
          ref={popoverRef}
          className={twx(
            'absolute top-full mt-1.5 bg-white border border-content-border rounded-2xl shadow-xl z-prompt overflow-hidden',
            align === 'right' ? 'right-0' : 'left-0',
            widthClasses[width],
          )}
        >
          {/* Pass close() down so action elements can dismiss after firing. */}
          <MenuPopoverContext.Provider value={() => setIsOpen(false)}>
            {children}
          </MenuPopoverContext.Provider>
        </div>
      )}
    </div>
  );
};

import { createContext, useContext } from 'react';

const MenuPopoverContext = createContext<(() => void) | null>(null);

/** Inside a MenuPopover body, call this to dismiss the popover (e.g. after
 * a menu item executes its action). Returns a no-op outside the popover so
 * call sites don't have to guard. */
export const useMenuPopoverClose = (): (() => void) => {
  const close = useContext(MenuPopoverContext);
  return close ?? (() => {});
};
